import { useState, useEffect, useCallback } from "react";
import { apiRequest } from "../../lib/http.ts";

interface Group {
  id: string;
  chatId: string;
  name: string;
  role: "source" | "destination" | "both";
  active: boolean;
  offersCount: number;
  lastMessageAt: string | null;
}

export function GroupsPage() {
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [newGroup, setNewGroup] = useState({ chatId: "", name: "", role: "source" as const });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await apiRequest<{ items: Group[] }>("/api/v1/groups");
      setGroups(result.items ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao carregar grupos.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (group: Group) => {
    setActionBusy(group.id);
    try {
      await apiRequest(`/api/v1/groups/${group.id}`, {
        method: "PATCH",
        body: JSON.stringify({ active: !group.active }),
      });
      await load();
    } catch {
      setError("Erro ao atualizar grupo.");
    } finally {
      setActionBusy(null);
    }
  };

  const createGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    setActionBusy("new");
    try {
      await apiRequest("/api/v1/groups", {
        method: "POST",
        body: JSON.stringify(newGroup),
      });
      setShowForm(false);
      setNewGroup({ chatId: "", name: "", role: "source" });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao criar grupo.");
    } finally {
      setActionBusy(null);
    }
  };

  const syncGroups = async () => {
    setActionBusy("sync");
    try {
      await apiRequest("/api/v1/integrations/waha/sync-groups", { method: "POST" });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao sincronizar.");
    } finally {
      setActionBusy(null);
    }
  };

  const roleLabel: Record<string, string> = {
    source: "Origem",
    destination: "Destino",
    both: "Ambos",
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <button
          onClick={syncGroups}
          disabled={actionBusy === "sync"}
          className="rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground disabled:opacity-50"
        >
          Sincronizar com WAHA
        </button>
        <button
          onClick={() => setShowForm(!showForm)}
          className="rounded-md border px-3 py-2 text-sm"
        >
          {showForm ? "Cancelar" : "Novo Grupo"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={createGroup} className="rounded-lg border bg-card p-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <input
              placeholder="Chat ID (ex: 1203630...@g.us)"
              value={newGroup.chatId}
              onChange={(e) => setNewGroup({ ...newGroup, chatId: e.target.value })}
              required
              className="rounded-md border bg-background px-3 py-2 text-sm"
            />
            <input
              placeholder="Nome do grupo"
              value={newGroup.name}
              onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
              required
              className="rounded-md border bg-background px-3 py-2 text-sm"
            />
            <select
              value={newGroup.role}
              onChange={(e) => setNewGroup({ ...newGroup, role: e.target.value as "source" | "destination" | "both" })}
              className="rounded-md border bg-background px-3 py-2 text-sm"
            >
              <option value="source">Origem</option>
              <option value="destination">Destino</option>
              <option value="both">Ambos</option>
            </select>
          </div>
          <button
            type="submit"
            disabled={actionBusy === "new"}
            className="mt-3 rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground disabled:opacity-50"
          >
            Criar Grupo
          </button>
        </form>
      )}

      {error && (
        <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-14 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      ) : groups.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          Nenhum grupo configurado. Clique em "Sincronizar com WAHA" para importar.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-left text-sm">
            <thead className="bg-muted">
              <tr>
                <th className="px-3 py-2 font-medium">Nome</th>
                <th className="px-3 py-2 font-medium">Tipo</th>
                <th className="px-3 py-2 font-medium">Ofertas</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Ações</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => (
                <tr key={group.id} className="border-t">
                  <td className="px-3 py-2">
                    <div className="font-medium">{group.name}</div>
                    <div className="font-mono text-xs text-muted-foreground">{group.chatId}</div>
                  </td>
                  <td className="px-3 py-2">
                    <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
                      {roleLabel[group.role]}
                    </span>
                  </td>
                  <td className="px-3 py-2">{group.offersCount}</td>
                  <td className="px-3 py-2">
                    <span className={`rounded-full px-2 py-0.5 text-xs ${group.active ? "bg-green-500/10 text-green-700" : "bg-red-500/10 text-red-700"}`}>
                      {group.active ? "Ativo" : "Inativo"}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <button
                      onClick={() => toggleActive(group)}
                      disabled={actionBusy === group.id}
                      className="rounded border px-2 py-1 text-xs disabled:opacity-50"
                    >
                      {group.active ? "Desativar" : "Ativar"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
